import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Plus } from "lucide-react";

const empty = {
  leave_type: "casual",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: new Date().toISOString().slice(0, 10),
  reason: "",
};

export default function MyLeaves() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try { const { data } = await api.get("/me/leaves"); setList(data); } catch {}
  };
  useEffect(() => { load(); }, []);

  const apply = async () => {
    try {
      await api.post("/me/leaves", form);
      toast.success("Leave applied");
      setOpen(false);
      setForm(empty);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="space-y-6" data-testid="me-leaves">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] tracking-widest uppercase text-zinc-500 mb-2">My space</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">My Leaves</h1>
          <p className="text-sm text-zinc-500 mt-1">Apply for time off and view your history.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="me-apply-leave" className="btn-pressable rounded-sm h-11 bg-[#F97316] hover:bg-[#EA580C] text-white font-mono uppercase tracking-widest text-xs px-5">
              <Plus className="h-4 w-4 mr-2" /> Apply leave
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-sm max-w-lg">
            <DialogHeader><DialogTitle className="font-heading text-xl tracking-tight">Apply for leave</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <Field label="Type">
                <Select value={form.leave_type} onValueChange={(v) => setForm({ ...form, leave_type: v })}>
                  <SelectTrigger className="rounded-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="casual">Casual</SelectItem>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="earned">Earned</SelectItem>
                    <SelectItem value="shore">Shore (Seafarer)</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Start"><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="rounded-sm" /></Field>
                <Field label="End"><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="rounded-sm" /></Field>
              </div>
              <Field label="Reason"><Textarea rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="rounded-sm" /></Field>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
              <Button onClick={apply} className="btn-pressable rounded-sm bg-[#09090B] hover:bg-zinc-800 text-white font-mono uppercase tracking-widest text-xs">Submit</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-zinc-200 rounded-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-zinc-50 hover:bg-zinc-50">
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Type</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Dates</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Reason</TableHead>
              <TableHead className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.length === 0 && (
              <TableRow><TableCell colSpan={4} className="text-center py-12 text-sm text-zinc-500">No leave applications yet.</TableCell></TableRow>
            )}
            {list.map((lv) => (
              <TableRow key={lv.id} className="hover:bg-zinc-50">
                <TableCell className="font-mono text-xs uppercase tracking-widest">{lv.leave_type}</TableCell>
                <TableCell className="font-mono text-xs">{lv.start_date} → {lv.end_date}</TableCell>
                <TableCell className="text-xs text-zinc-600 max-w-xs truncate">{lv.reason || "—"}</TableCell>
                <TableCell>
                  <span className={`font-mono text-[10px] uppercase tracking-widest px-2 py-1 rounded-sm ${
                    lv.status === "approved" ? "bg-emerald-100 text-emerald-700" :
                    lv.status === "rejected" ? "bg-red-100 text-red-700" :
                    "bg-amber-100 text-amber-700"
                  }`}>{lv.status}</span>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[10px] uppercase tracking-widest font-mono text-zinc-500">{label}</Label>
      {children}
    </div>
  );
}
